import * as EventActions from "src/app/store/event/actions";
import * as FromUser from "src/app/store/user/selectors";
import * as FromEvent from "src/app/store/event/selectors";
import { ChangeDetectionStrategy, Component, OnInit } from "@angular/core";
import { EventContentDto, EventDto, UserDto } from "@api";
import { Store } from "@ngrx/store";
import { Observable } from "rxjs";
import { AppRoutes, EVENTS_PER_PAGE } from "src/app/constants";
import { RootState } from "src/app/store";
import { AlertController, InfiniteScrollCustomEvent, IonRouterOutlet, ModalController } from "@ionic/angular";
import { FormControl, FormGroup } from "@angular/forms";
import { EventPage } from "src/app/modals/event/event.page";
import { IframeModal } from "src/app/modals/iframe-modal/iframe-modal.page";
import { TranslateService } from "@ngx-translate/core";
import { Browser } from "@capacitor/browser";
import { first } from "rxjs/operators";

@Component({
  selector: "app-events",
  templateUrl: "./events.page.html",
  styleUrls: ["./events.page.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventsPage implements OnInit {
  public routes: typeof AppRoutes = AppRoutes;
  public presentingElement: HTMLElement;
  public form: FormGroup;
  public checkUpType: EventContentDto.TypeEnum = EventContentDto.TypeEnum.CheckUp;
  public user$: Observable<UserDto> = this.store.select(FromUser.selectUser);
  public events$: Observable<EventDto[]> = this.store.select(FromEvent.selectAllEvents);
  public currentPage$: Observable<number> = this.store.select(FromEvent.selectCurrentPage);
  public totalPages$: Observable<number> = this.store.select(FromEvent.selectTotalPages);
  public breadcrumbs = [{ name: "Dashboard", link: `/${this.routes.home}` }, { name: "Veranstaltungen" }];

  constructor(
    private store: Store<RootState>,
    private routerOutlet: IonRouterOutlet,
    private modalController: ModalController,
    private alertController: AlertController,
    private i18n: TranslateService,
  ) {
    this.form = new FormGroup({
      search: new FormControl(""),
      module: new FormControl(null),
    });
  }

  public ngOnInit(): void {
    this.presentingElement = this.routerOutlet.nativeEl;
  }

  public trackBy(_: number, event: EventDto): string {
    return event.id;
  }

  public handleModuleSelectorChanged(event: Event): void {
    if ((event as any).detail.value === "Alle") {
      (event.target as any).value = null;
    }
  }

  public async handleEventClick(event: EventDto): Promise<void> {
    const modal = await this.modalController.create({
      component: EventPage,
      componentProps: { event },
      presentingElement: this.routerOutlet.nativeEl,
      cssClass: ["event-detail-modal"],
      swipeToClose: true,
    });

    await modal.present();
  }

  public async rescheduleCheckUp(event: EventDto): Promise<void> {
    const url = event.externalLink;

    if (!url) {
      const alert = await this.alertController.create({
        header: this.i18n.instant("EVENT_RESPONSE.RESCHEDULE_ALERT_HEADER"),
        message: this.i18n.instant("EVENT_RESPONSE.RESCHEDULE_ALERT_MESSAGE"),
        buttons: [{ role: "cancel", text: this.i18n.instant("EVENT_RESPONSE.RESCHEDULE_ALERT_BUTTON") }],
      });

      return alert.present();
    }

    const modal = await this.modalController.create({
      component: IframeModal,
      componentProps: { url: event.externalLink },
      presentingElement: this.routerOutlet.nativeEl,
      cssClass: ["event-reschedule-modal"],
      swipeToClose: true,
    });

    await modal.present();
  }

  public joinEvent(eventDto: EventDto, event: Event): void {
    event.stopPropagation();
    Browser.open({ url: eventDto.location });
  }

  public loadMoreEvents(event: InfiniteScrollCustomEvent): void {
    this.currentPage$
      .pipe(first())
      .subscribe(currentPage => this.store.dispatch(EventActions.getEvents({ limit: EVENTS_PER_PAGE, page: currentPage + 1, event })));
  }

  protected ionViewDidEnter(): void {
    this.store.dispatch(EventActions.getEvents({ limit: EVENTS_PER_PAGE, page: 1 }));
    this.store.dispatch(EventActions.getReferents());
  }
}
